// Centralized Axios API instance with integrated token refresh
// Import and use `api` instead of the global axios in your feature code:
//   import { api } from './api/client';
//   const resp = await api.get('/accounts');

import { createHttpClient } from '../auth/httpClient';
import { createTokenManager } from '../auth/tokenManager';
import config from '../config';
import Auth from '../api/auth';
import tokenStorage from '../utils/tokenStorage';
import { TOKEN_SKEW_MS } from '../config/auth.config';
import { attachCorrelationIdHeader } from '../libs/correlationId';

// Token manager for this client instance (functional)
const tokenManager = createTokenManager({
  refreshFn: () => Auth.refreshToken(),
  getAuthData: () => (typeof Auth.getAuthData === 'function' ? Auth.getAuthData() : null),
  setAuthData: (tokenInfo) => {
    if (typeof Auth.setAuthData === 'function') {
      // Extract token data from the refresh response
      // Auth.setAuthData expects: (token, userName, refreshToken, userImpersonation)
      // But tokenInfo from refreshToken() is: { access_token, refresh_token, userName, ... }
      const currentAuth = Auth.getAuthData();
      Auth.setAuthData(
        tokenInfo.access_token,
        currentAuth?.unique_name || tokenInfo.userName,
        tokenInfo.refresh_token || currentAuth?.refreshToken, // Fallback to stored refresh token when API omits it
        currentAuth?.userImpersonation || false
      );
    }
  },
  clearAuthData: () => tokenStorage.clear(),
  skewMs: TOKEN_SKEW_MS, // proactive refresh skew
  onLogout: () => { /* host app may hook logout globally if desired */ },
});

// Create a dedicated axios-like client
export const api = createHttpClient({
  baseURL: config.apiUrl,
  tokenManager,
  authlessPaths: ['/auth/login','/auth/refresh','/auth/logout'],
  maxReplays: 20,
  retry: { retries: 3, baseMs: 1000, capMs: 8000 },
});

// Send the open page's correlation id as X-Correlation-ID so API and ReadyDocs log lines join the page events.
const apiOrigin = (() => {
  try {
    return new URL(config.apiUrl).origin;
  } catch {
    return '';
  }
})();
api.interceptors.request.use((requestConfig) => attachCorrelationIdHeader(requestConfig, apiOrigin));

// Optional: expose helpers if consumers need them
export const abortAll = () => api.abortAll?.();
export const getTokenManager = () => tokenManager;
